import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';
  const {
    language,
    setLanguage,
    t
  } = useLanguage();
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const navLinks = [{
    name: t('nav.about'),
    href: isHome ? '#about' : '/#about'
  }, {
    name: t('nav.focus'),
    href: isHome ? '#focus' : '/#focus'
  }, {
    name: t('nav.whyUs'),
    href: isHome ? '#why-us' : '/#why-us'
  }];
  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'es' : 'en');
  };
  return <header className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white/95 backdrop-blur-md border-b border-gold/20 py-3' : 'bg-white border-b border-transparent py-5'}`} data-id="element-52">
      <div className="container mx-auto px-6 md:px-12 grid grid-cols-3 items-center" data-id="element-53">
        {/* Left — Wordmark */}
        <Link to="/" className="hidden md:flex flex-col leading-none" aria-label="Paola Skin Lab — Home" data-id="element-54">
          <span className="font-serif italic text-2xl lg:text-3xl text-[#0A0A0A]" data-id="element-55">
            Paola
          </span>
          <span className="text-[10px] lg:text-xs tracking-[0.4em] uppercase text-black/70 mt-1" data-id="element-56">
            Skin Lab
          </span>
        </Link>

        {/* Mobile Left placeholder (keeps grid balance) */}
        <div className="md:hidden" data-id="element-57" />

        {/* Center — Logo Image (bigger) */}
        <div className="flex justify-center" data-id="element-58">
          <Link to="/" aria-label="Paola Skin Lab — Home" className="block" data-id="element-59">
            <img src="https://cdn.magicpatterns.com/uploads/1CU99EPu21u9KJDfFRoM13/image.png" alt="Paola Skin Lab" className={`w-auto object-contain transition-all duration-300 ${isScrolled ? 'h-14 md:h-20' : 'h-16 md:h-24 lg:h-28'}`} data-id="element-60" />
          </Link>
        </div>

        {/* Right — Desktop Nav */}
        <nav className="hidden md:flex items-center justify-end gap-7" data-id="element-61">
          {navLinks.map(link => <a key={link.name} href={link.href} className="text-xs tracking-widest uppercase text-black/70 hover:text-gold transition-colors" data-id="element-62">
              {link.name}
            </a>)}
          <a href={isHome ? '#contact' : '/#contact'} className="text-xs tracking-widest uppercase px-4 py-2 border border-gold text-black hover:bg-gold hover:text-white transition-colors" data-id="element-63">
            {t('nav.contact')}
          </a>

          {/* Language Switcher Desktop */}
          <button onClick={toggleLanguage} className="text-xs tracking-widest uppercase text-black/70 hover:text-gold transition-colors flex items-center gap-1 ml-2" aria-label="Toggle language" data-id="element-64">
            <span className={language === 'en' ? 'font-medium text-black' : ''} data-id="element-65">
              EN
            </span>
            <span className="text-black/30" data-id="element-66">|</span>
            <span className={language === 'es' ? 'font-medium text-black' : ''} data-id="element-67">
              ES
            </span>
          </button>
        </nav>

        {/* Mobile Right Controls */}
        <div className="flex items-center justify-end gap-3 md:hidden" data-id="element-68">
          <button onClick={toggleLanguage} className="text-xs tracking-widest uppercase text-black/70 flex items-center gap-1" aria-label="Toggle language" data-id="element-69">
            <span className={language === 'en' ? 'font-medium text-black' : ''} data-id="element-70">
              EN
            </span>
            <span className="text-black/30" data-id="element-71">|</span>
            <span className={language === 'es' ? 'font-medium text-black' : ''} data-id="element-72">
              ES
            </span>
          </button>
          <button className="text-black p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu" data-id="element-73">
            {mobileMenuOpen ? <X strokeWidth={1.25} data-id="element-74" /> : <Menu strokeWidth={1.25} data-id="element-75" />}
          </button>
        </div>
      </div>

      {/* Mobile Nav */}
      <AnimatePresence data-id="element-76">
        {mobileMenuOpen && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} className="absolute top-full left-0 w-full bg-white border-b border-black/5 shadow-xl md:hidden" data-id="element-77">
            <nav className="flex flex-col py-6 px-6 gap-6" data-id="element-78">
              <div className="flex flex-col leading-none mb-2" data-id="element-79">
                <span className="font-serif italic text-2xl text-[#0A0A0A]" data-id="element-80">
                  Paola
                </span>
                <span className="text-[10px] tracking-[0.4em] uppercase text-black/70 mt-1" data-id="element-81">
                  Skin Lab
                </span>
              </div>
              {navLinks.map(link => <a key={link.name} href={link.href} onClick={() => setMobileMenuOpen(false)} className="text-sm tracking-widest uppercase text-black/80" data-id="element-82">
                  {link.name}
                </a>)}
              <a href={isHome ? '#contact' : '/#contact'} onClick={() => setMobileMenuOpen(false)} className="text-sm tracking-widest uppercase text-gold" data-id="element-83">
                {t('nav.contact')}
              </a>
            </nav>
          </motion.div>}
      </AnimatePresence>
    </header>;
}